import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuLoader, LuFileUp } from 'react-icons/lu';
import {
  filterActiveImportJobs,
  importStageLabel,
  parseImportListResponse,
  sortActiveImportJobs,
} from '../../utils/importJobStatus';
import { importKicker, importSubtitle } from '../../utils/importSourceLabels';

const POLL_MS = 2500;

async function fetchActiveImports(apiClient) {
  try {
    const res = await apiClient.get('/api/v1/learn/imports/active');
    return parseImportListResponse(res.data);
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404 || status === 405) {
      const fallback = await apiClient.get('/api/v1/learn/imports');
      return parseImportListResponse(fallback.data);
    }
    throw err;
  }
}

export default function LearnHubImportQueue({ apiClient }) {
  const [jobs, setJobs] = useState([]);
  const [fetchError, setFetchError] = useState('');

  const refresh = useCallback(async () => {
    if (!apiClient) return;
    try {
      const rows = await fetchActiveImports(apiClient);
      setJobs(sortActiveImportJobs(filterActiveImportJobs(rows)));
      setFetchError('');
    } catch {
      setFetchError('');
      try {
        const fallback = await apiClient.get('/api/v1/learn/imports');
        const rows = parseImportListResponse(fallback.data);
        setJobs(sortActiveImportJobs(filterActiveImportJobs(rows)));
      } catch {
        setFetchError('Could not load import status.');
      }
    }
  }, [apiClient]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => refresh();
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refresh]);

  if (fetchError && jobs.length === 0) {
    return (
      <p className="learn-hub-import-queue learn-hub-import-queue--error" role="status">
        {fetchError}
      </p>
    );
  }

  if (jobs.length === 0) {
    return null;
  }

  const longCount = jobs.filter((j) => j.longRunning || j.status === 'QUEUED').length;

  return (
    <section className="learn-hub-import-queue" aria-labelledby="learn-hub-queue-heading" aria-live="polite">
      <header className="learn-hub-import-queue__head">
        <div className="learn-hub-import-queue__head-text">
          <p id="learn-hub-queue-heading" className="learn-hub-import-queue__title">
            <LuLoader className="learn-import-spinner learn-hub-import-queue__spin" aria-hidden />
            Background imports
          </p>
          <p className="learn-muted learn-hub-import-queue__sub">
            {jobs.length} active
            {longCount > 0 ? ` · ${longCount} queued or long-running` : ''}
            {' · '}
            live
          </p>
        </div>
        <Link to="/hub/imports" className="home-btn home-btn-secondary learn-btn--compact">
          Import history
        </Link>
      </header>

      <ul className="learn-hub-import-queue__list">
        {jobs.map((job) => {
          const pct = Math.min(100, Math.max(0, Number(job.progress) || 0));
          const needsReview = job.status === 'PENDING_REVIEW';
          const queued = job.status === 'QUEUED';
          const notebookHref = job.notebookId ? `/hub/notebooks/${job.notebookId}` : null;
          return (
            <li
              key={job.id}
              className={`learn-hub-import-queue__item${job.longRunning || queued ? ' is-long' : ''}`}
            >
              <div className="learn-hub-import-queue__item-main">
                <span className="learn-hub-import-queue__type" aria-hidden>
                  <LuFileUp />
                </span>
                <div className="learn-hub-import-queue__copy">
                  <p className="learn-hub-import-queue__notebook">
                    {job.notebookTitle || 'Notebook'}
                  </p>
                  <p className="learn-hub-import-queue__meta">
                    <span>{importKicker(job)}</span>
                    <span className="learn-hub-import-queue__sep" aria-hidden>·</span>
                    <span className="learn-hub-import-queue__stage">{importStageLabel(job)}</span>
                    {!needsReview && !queued && (
                      <>
                        <span className="learn-hub-import-queue__sep" aria-hidden>·</span>
                        <span>{pct}%</span>
                      </>
                    )}
                    {queued && (
                      <>
                        <span className="learn-hub-import-queue__sep" aria-hidden>·</span>
                        <span>waiting</span>
                      </>
                    )}
                    {job.pageFrom != null && job.pageTo != null && (
                      <>
                        <span className="learn-hub-import-queue__sep" aria-hidden>·</span>
                        <span>pp. {job.pageFrom}–{job.pageTo}</span>
                      </>
                    )}
                  </p>
                  <p className="learn-muted learn-hub-import-queue__source">{importSubtitle(job)}</p>
                  {(job.queueMessage || job.queuedJobsAhead > 0) && (
                    <p className="learn-hub-import-queue__queue-msg">
                      {job.queueMessage
                        || `${job.queuedJobsAhead} import(s) ahead in the queue.`}
                    </p>
                  )}
                  {!needsReview && (
                    <div
                      className="learn-progress learn-progress--import learn-hub-import-queue__bar"
                      role="progressbar"
                      aria-valuenow={queued ? 0 : pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={queued ? 'Import queued' : `Import progress ${pct} percent`}
                    >
                      <span
                        className="learn-progress-fill"
                        style={{ width: `${queued ? Math.max(pct, 8) : pct}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
              {notebookHref && (
                <Link
                  to={notebookHref}
                  className="home-btn home-btn-primary learn-btn--compact learn-hub-import-queue__open"
                >
                  {needsReview ? 'Review' : queued ? 'View' : 'Open'}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
